import React, { useCallback, useRef, useState } from 'react';
import { Flex, TextV2 } from '@withjoy/joykit';
import { AdminSidebarComponentProps } from '../../../JsonComponent.types';
import { useGoToReorderView } from '../../../hooks/useJsonComponentNavigation';
import { useTranslation } from '@shared/core';
import { VIPEditor } from '@shared/components/VIPEditor/VIPEditor';
import { BottomBar } from '../../../shared/BottomBar';
import { useVIPList } from '../hooks/useVIPList';
import { AddCircle } from '@withjoy/joykit/icons';
import { useUpdateWebsitePreview } from '@website-editor/hooks/useUpdateWebsitePreview';
import { ProgressiveBlur } from '@shared/components/ProgressiveBlur/ProgressiveBlur';
import { VIPList } from '../components/VIPList';
import { usePresentationDataState } from '../hooks/usePresentationDataState';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { useDeleteVip } from '../hooks/useDeleteVIP';

type ViewState = 'list' | 'edit' | 'add';

export const VipEdit = (props: AdminSidebarComponentProps) => {
  const { data } = props;
  const { goToReorderView } = useGoToReorderView();
  const { tq } = useTranslation('VIPCustomComponent');
  const [editId, setEditId] = useState<string | null>(null);
  const [viewState, setViewState] = useState<ViewState>('list');
  const vipFormRef = useRef<{ submit: () => void }>(null);
  const { data: vipData, loading, refetch } = useVIPList(props.data.eventId);
  const { updateWebsitePreview } = useUpdateWebsitePreview();
  const { isFiltering, setIsFiltering, handleFilterClick, handleReorder, presentationData, savePresentationData, revertPresentationData } = usePresentationDataState(
    data,
    vipData ?? [],
  );
  const [confirmationState, setConfirmationState] = useState<'delete' | 'exit'>('exit');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { deleteVIP } = useDeleteVip();

  const handleBackClicked = () => {
    if (isFiltering) {
      setConfirmationState('exit');
      setShowConfirmation(true);
    } else if (viewState === 'list') {
      goToReorderView();
    } else {
      setConfirmationState('exit');
      setShowConfirmation(true);
    }
  };

  // Trigger the form editor to submit
  // when we press the submit button here.
  const handleSubmitClicked = async () => {
    // Error handling happens inside VIPForm.
    await vipFormRef.current?.submit();
    refetch();
    updateWebsitePreview();
  };

  // Confirmation dialog close action
  const handleConfirmationClose = useCallback(() => {
    setShowConfirmation(false);
    setEditId(null);
  }, []);

  // confirmation dialog confirm action
  const handleConfirmationAction = useCallback(async () => {
    if (confirmationState === 'delete' && editId) {
      // Confirm delete VIP
      await deleteVIP(editId);
      refetch();
      updateWebsitePreview();
      setViewState('list');
      setEditId(null);
    } else if (confirmationState === 'exit') {
      if (isFiltering) {
        // Confirm exit filtering without saving
        setIsFiltering(false);
        revertPresentationData();
        setShowConfirmation(false);
      } else {
        // Confirm exit without saving
        setViewState('list');
        setEditId(null);
      }
    }
    setShowConfirmation(false);
  }, [confirmationState, deleteVIP, editId, isFiltering, refetch, revertPresentationData, setIsFiltering, updateWebsitePreview]);

  const renderContent = useCallback(() => {
    switch (viewState) {
      case 'list':
        return (
          <VIPList
            vipList={vipData}
            presentationData={{
              sortOrder: presentationData.sortOrder ?? [],
              excludedIDs: presentationData.excludedIDs ?? [],
            }}
            data={data}
            loading={loading}
            onClickVip={id => {
              setEditId(id);
              setViewState('edit');
            }}
            onDeleteClick={async id => {
              setEditId(id);
              setConfirmationState('delete');
              setShowConfirmation(true);
            }}
            handleFilterClick={handleFilterClick}
            handleReorder={handleReorder}
            isFiltering={isFiltering}
          />
        );
      case 'edit':
      case 'add':
        return (
          <VIPEditor
            ref={vipFormRef}
            eventId={props.data.eventId}
            state={viewState}
            editId={editId ?? ''}
            onSubmit={() => {
              setViewState('list');
              setEditId(null);
            }}
          />
        );
      default:
        return null;
    }
  }, [data, editId, handleFilterClick, handleReorder, isFiltering, loading, presentationData.excludedIDs, presentationData.sortOrder, props.data.eventId, viewState, vipData]);

  return (
    <>
      <Flex
        flexDirection="column"
        padding="1rem"
      >
        <Flex
          justifyContent="space-between"
          marginBottom="1rem"
          paddingX="0.5rem"
        >
          <TextV2 typographyVariant="hed6">{tq`VIPs`}</TextV2>
          {!isFiltering && viewState === 'list' && (
            <TextV2
              marginY="auto"
              cursor="pointer"
              onClick={() => setIsFiltering(true)}
            >{tq`Filter`}</TextV2>
          )}
        </Flex>

        {renderContent()}
        <BottomBar
          actions={[
            {
              label: tq`Cancel`,
              variant: 'primary',
              onClick: handleBackClicked,
            },
            {
              label: tq`Add VIP`,
              variant: 'secondary',
              onClick: () => setViewState('add'),
              hidden: viewState !== 'list' || isFiltering,
              buttonProps: {
                startIcon: <AddCircle />,
              },
            },
            {
              label: tq`Save VIP`,
              variant: 'secondary',
              onClick: handleSubmitClicked,
              hidden: viewState === 'list',
            },
            {
              label: tq`Save`,
              variant: 'secondary',
              onClick: () => {
                savePresentationData();
                setIsFiltering(false);
                updateWebsitePreview();
              },
              hidden: !isFiltering,
            },
          ]}
        >
          <ProgressiveBlur containerProps={{ right: '16px' }} />
        </BottomBar>
      </Flex>
      <ConfirmationDialog
        isOpen={showConfirmation}
        onClose={handleConfirmationClose}
        onConfirm={handleConfirmationAction}
        title={confirmationState === 'delete' ? tq`Delete VIP?` : tq`Exit without saving?`}
        message={
          confirmationState === 'delete'
            ? tq`Are you sure you want to delete this VIP? This action cannot be undone.`
            : tq`Are you sure you want to exit without saving? All unsaved changes will be lost.`
        }
        confirmText={confirmationState === 'delete' ? tq`Delete VIP` : tq`Exit without saving`}
        cancelText={tq`Cancel`}
      />
    </>
  );
};
